import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import { useAsyncEffect } from "@/utils"
import { useState } from "react"
import { api_fetch } from "../api/client.tsx"
import { parseISO, differenceInDays } from "date-fns"

export interface ReportStatus {
  last_report_date: string | null
  last_report_s3_uri: string | null
  last_request_date: string | null
  email_integrations_count: number
}

interface RowProps {
  title: string
  children: React.ReactNode
}

export function Row({ title, children }: RowProps) {
  return (
    <div class="flex justify-between items-center py-1">
      <span class="text-sm font-medium">{title}</span>
      <div class="text-sm text-gray-900">{children}</div>
    </div>
  )
}

export function formatDate(dateString: string) {
  try {
    return new Date(dateString).toLocaleDateString()
  } catch {
    return "Unknown"
  }
}

function getStatusDisplay(status: ReportStatus, notReceivingText: string) {
  if (status.last_report_date) {
    return { color: "green", text: "Receiving Reports" }
  } else if (status.last_request_date) {
    return { color: "yellow", text: "Pending" }
  } else {
    return { color: "red", text: notReceivingText }
  }
}

export interface ReportSettingsConfig {
  statusEndpoint: string
  requestEndpoint: string
  requestButtonText: string
  requestingText: string
  notReceivingText: string
  setupRequiredText: string
  cooldownDays: number
}

interface ReportSettingsProps {
  config: ReportSettingsConfig
}

export function ReportSettings({ config }: ReportSettingsProps) {
  const [status, setStatus] = useState<ReportStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRequesting, setIsRequesting] = useState(false)

  useAsyncEffect(async () => {
    if (!isLoading) return
    const res = await api_fetch<ReportStatus>(config.statusEndpoint)
    setIsLoading(false)
    if (res.ok) {
      setStatus(res.value.data)
    }
  }, [isLoading])

  async function handleRequestReport() {
    setIsRequesting(true)
    const res = await api_fetch<{ email_sent: boolean; message: string }>(config.requestEndpoint, {
      method: "POST",
      body: {},
    })
    setIsRequesting(false)
    if (res.ok) {
      toast({
        title: "Request Sent",
        description: res.value.data.message,
      })
      setIsLoading(true)
    } else {
      toast({
        title: "Request Failed",
        description: res.error.message,
        variant: "destructive",
      })
    }
  }

  if (isLoading) {
    return <div class="text-gray-500">Loading...</div>
  }

  if (!status) {
    return <div class="text-gray-500">Failed to load status</div>
  }

  const lastRequest = status.last_request_date ? parseISO(status.last_request_date) : null
  const isRequestRecent = lastRequest ? differenceInDays(new Date(), lastRequest) < config.cooldownDays : false
  const shouldShowRequestButton = status.email_integrations_count > 0
  const statusDisplay = getStatusDisplay(status, config.notReceivingText)

  return (
    <div class="space-y-2">
      <Row title="Status">
        <div class="flex items-center gap-2">
          <div class={`w-3 h-3 rounded-full bg-${statusDisplay.color}-500`} />
          <span class={`text-${statusDisplay.color}-700`}>{statusDisplay.text}</span>
        </div>
      </Row>

      {status.last_report_date && (
        <Row title="Last Report Received">
          <div class="flex items-center gap-3">
            <span>{formatDate(status.last_report_date)}</span>
            {status.last_report_s3_uri && (
              <Button variant="outline" size="sm" asChild>
                <a
                  href={`/api/_s3?s3_uri=${encodeURIComponent(status.last_report_s3_uri)}`}
                  target="_blank"
                  rel="noopener noreferrer">
                  View Report
                </a>
              </Button>
            )}
          </div>
        </Row>
      )}

      {status.last_request_date && <Row title="Last Request Sent">{formatDate(status.last_request_date)}</Row>}

      {shouldShowRequestButton && (
        <Row title="Request Reports">
          <div class="flex items-center gap-2">
            <Button
              onClick={handleRequestReport}
              disabled={isRequesting || isRequestRecent}
              variant="outline"
              class={isRequestRecent ? "opacity-50 cursor-not-allowed" : ""}>
              {isRequesting ? config.requestingText : config.requestButtonText}
            </Button>
            {isRequestRecent && (
              <span class="text-xs text-gray-500">Available {config.cooldownDays} days after last request</span>
            )}
          </div>
        </Row>
      )}

      {status.email_integrations_count === 0 && (
        <Row title="Setup Required">
          <div class="text-amber-600 text-sm">{config.setupRequiredText}</div>
        </Row>
      )}
    </div>
  )
}
